import React, { useState, useRef, useCallback, MouseEvent, useEffect, ReactElement } from 'react';
import cn from 'classnames';
import { RichUtils, Editor } from 'draft-js';
import { isAndroid } from 'react-device-detect';

import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpButton from 'Wpds/components/button/WpButton';
import { asyncForEach } from 'Shared/helpers';
import { FeedbackCommentType } from 'Shared/constants/types';
import ImageDndUploader from 'Shared/components/ImageDndUploader/ImageDndUploader';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';
import VideoRecorder from 'Shared/components/VideoRecorder/VideoRecorder';
import { enrichEditorStateByImageAttachment } from 'Shared/components/DraftEditor/plugins/imageAttachmentsPlugin';

import SidePanelTextEditorToolbar from './SidePanelTextEditorToolbar/SidePanelTextEditorToolbar';
import './SidePanelTextEditor.scss';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  showSaveButton?: boolean;
  toolbarPlacement?: 'top' | 'bottom';
  meetingItemUuid: string;
  editorState: Editor;
  commentForUpdate?: FeedbackCommentType;
  onEditorChange: Function;
  onAttachmentInputChange: Function;
  onAssignedPersonAdd: Function;
  onSaveVideo: Function;
  onSubmit: () => void;
  onGifAdd: Function;
  onEmojiAdd: Function;
  onUpdateComment?: (event: MouseEvent<HTMLButtonElement>) => void;
  onCancel?: (event: MouseEvent<HTMLButtonElement>) => void;
  focused?: boolean;
  onFocus?: Function;
  onBlur?: Function;
  onClear?: Function;
  cleared?: boolean;
  hideVideo?: boolean;
  showGif?: boolean;
  showEmoji?: boolean;
  showAttachment?: boolean;
  autoFocus?: boolean;
  isSaving?: boolean;
  placeholder?: string;
  hint?: React.ReactNode;
  enterToSend?: boolean;
  enableMentions?: boolean;
  workspaceId?: string;
};

const DEFAULT_PLACEHOLDER = 'Leave a comment...';
const DEFAULT_TOOLBAR_PLACEMENT = 'top';

function SidePanelTextEditor({
  showSaveButton = false,
  isSaving,
  placeholder = DEFAULT_PLACEHOLDER,
  toolbarPlacement = DEFAULT_TOOLBAR_PLACEMENT,
  meetingItemUuid,
  editorState,
  onEditorChange,
  onAttachmentInputChange,
  onAssignedPersonAdd,
  onSaveVideo,
  onCancel,
  onSubmit,
  onGifAdd,
  onEmojiAdd,
  onUpdateComment,
  commentForUpdate,
  hideVideo,
  autoFocus,
  hint,
  enterToSend = true,
  workspaceId,
  showGif,
  showEmoji,
  showAttachment,
  enableMentions,
}: Props): ReactElement {
  const [isFocused, setIsFocused] = useState(false);
  const [isVideoRecorderOpen, setIsVideoRecorderOpen] = useState(false);
  const attachmentInputEl = useRef(null);
  const draftEditorEl = useRef(null);
  const { tr } = useTranslation();

  const handleSaveVideoComment = useCallback(video => onSaveVideo(video), [onSaveVideo]);

  const handleVideoRecorderToggle = useCallback(() => {
    setIsVideoRecorderOpen(!isVideoRecorderOpen);
  }, [isVideoRecorderOpen]);

  const handleAttachmentIconClick = useCallback(() => attachmentInputEl.current.click(), []);

  const handleAttachmentInputChange = useCallback(
    async ({ target: { files } }) => {
      if (!files.length) return;
      onAttachmentInputChange(files[0]);
    },
    [onAttachmentInputChange]
  );

  const focusTextEditor = useCallback(() => {
    setIsFocused(true);
    if (draftEditorEl.current) draftEditorEl.current.focus();
  }, [draftEditorEl]);

  const handleCommentCreateClick = useCallback(() => {
    onSubmit();
    if (isAndroid) setTimeout(draftEditorEl.current?.blur);
  }, [onSubmit, draftEditorEl.current]);

  const handleEmojiAdd = useCallback(
    emoji => {
      focusTextEditor();
      onEmojiAdd(emoji);
    },
    [focusTextEditor, onEmojiAdd]
  );

  const handleKeyCommandActionsMap = useCallback(() => {
    if (enterToSend) {
      return {
        'save-block': handleCommentCreateClick,
        'ctrl+enter': () => onEditorChange(RichUtils.insertSoftNewline(editorState)),
      };
    } else {
      return {
        'save-block': () => onEditorChange(RichUtils.insertSoftNewline(editorState)),
      };
    }
  }, [editorState, handleCommentCreateClick, onEditorChange, enterToSend]);

  const handlePastedFiles = useCallback(
    async files => {
      const file = files[0];
      if (!file) return;
      onEditorChange(await enrichEditorStateByImageAttachment(editorState, file));
    },
    [editorState, onEditorChange]
  );

  const handleFileDrop = useCallback(
    async files => {
      let enrichedEditorState = editorState;
      await asyncForEach(files, async file => {
        enrichedEditorState = await enrichEditorStateByImageAttachment(enrichedEditorState, file);
      });
      onEditorChange(enrichedEditorState);
    },
    [editorState, onEditorChange]
  );

  const handleGifAdd = useCallback(
    gif => {
      focusTextEditor();
      onGifAdd(gif);
    },
    [focusTextEditor, onGifAdd]
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  useEffect(() => {
    const node = draftEditorEl.current;
    if (node && autoFocus) setTimeout(focusTextEditor);
  }, []);

  const isToolbarTopPosition = toolbarPlacement === 'top';
  const isToolbarBottomPosition = toolbarPlacement === 'bottom';

  return (
    <>
      {isVideoRecorderOpen && <VideoRecorder onSave={handleSaveVideoComment} onHide={handleVideoRecorderToggle} />}

      {isToolbarTopPosition && (
        <SidePanelTextEditorToolbar
          showSaveButton={showSaveButton}
          isSaving={isSaving}
          hideVideo={hideVideo}
          commentForUpdate={commentForUpdate}
          attachmentInputEl={attachmentInputEl}
          onGifAdd={handleGifAdd}
          onEmojiAdd={handleEmojiAdd}
          onAttachmentIconClick={handleAttachmentIconClick}
          onVideoRecorderToggle={handleVideoRecorderToggle}
          onAttachmentInputChange={handleAttachmentInputChange}
          onCommentCreateClick={handleCommentCreateClick}
          hint={hint}
          showGif={showGif}
          showEmoji={showEmoji}
          showAttachment={showAttachment}
        />
      )}

      <div className="position-relative">
        <ImageDndUploader onFileDrop={handleFileDrop}>
          <div
            className={cn('side-panel-text-editor', { 'side-panel-text-editor--focused': isFocused })}
            onClick={focusTextEditor}
          >
            <DraftEditor
              isInlineAttachments
              meetingItemUuid={meetingItemUuid}
              placeholder={
                placeholder === DEFAULT_PLACEHOLDER ? tr('perform.common.editor.leave_a_comment') : placeholder
              }
              readOnly={!isFocused}
              editorRef={draftEditorEl}
              editorState={editorState}
              onKeyCommandActionsMap={handleKeyCommandActionsMap}
              handlePastedFiles={handlePastedFiles}
              onChange={onEditorChange}
              onAssignedPersonAdd={onAssignedPersonAdd}
              onBlur={handleBlur}
              showStyleToolbar
              workspaceId={workspaceId}
              enableMentions={enableMentions}
            />
          </div>
        </ImageDndUploader>
      </div>
      {commentForUpdate && (
        <div className="d-flex justify-content-end pt-2">
          <WpButtonGroup>
            <WpButton variant="tertiary" onClick={onCancel}>
              Cancel
            </WpButton>
            <WpButton onClick={onUpdateComment}>Save Changes</WpButton>
          </WpButtonGroup>
        </div>
      )}
      {isToolbarBottomPosition && (
        <SidePanelTextEditorToolbar
          showSaveButton={showSaveButton}
          isSaving={isSaving}
          hideVideo={hideVideo}
          commentForUpdate={commentForUpdate}
          attachmentInputEl={attachmentInputEl}
          onGifAdd={handleGifAdd}
          onEmojiAdd={handleEmojiAdd}
          onAttachmentIconClick={handleAttachmentIconClick}
          onVideoRecorderToggle={handleVideoRecorderToggle}
          onAttachmentInputChange={handleAttachmentInputChange}
          onCommentCreateClick={handleCommentCreateClick}
          hint={hint}
          showGif={showGif}
          showEmoji={showEmoji}
          showAttachment={showAttachment}
        />
      )}
    </>
  );
}

export default SidePanelTextEditor;
